import type { Messages } from '../i18n/messages';

/**
 * Scaffolder field-extension chrome (`ui:field` pickers used by the
 * self-service Promote/Release/config templates). `schema.title` /
 * `schema.description` supplied by the template YAML are server data and
 * stay raw — only the JS fallback copy used when a template doesn't set them
 * is keyed here. Gitops service/overlay/type names, resource quantities
 * (e.g. `250m`, `512Mi`) and REG ticket ids are raw domain/config data.
 *
 * Field labels that double as form-submission semantics (Type, Service,
 * Overlay, CPU tier, Memory tier, replica counts, custom request/limit
 * inputs) keep an identical `fun` value — these forms configure real
 * infrastructure, so playful relabeling risks confusing what a field
 * actually submits.
 */
export const templatesMessages: Messages = {
  // ExcludeServicesPicker
  'templates.excludeServicesPicker.label': {
    en: 'Exclude services',
    fun: 'Exclude services',
  },
  'templates.excludeServicesPicker.loadingServices': {
    en: 'Loading services…',
    fun: 'Rounding up services…',
  },
  'templates.excludeServicesPicker.placeholder': {
    en: 'Select services to skip…',
    fun: 'Pick who sits this one out…',
  },
  'templates.excludeServicesPicker.searchPlaceholder': {
    en: 'Search services…',
    fun: 'Search services…',
  },
  'templates.excludeServicesPicker.defaultHint': {
    en: 'No exclusions — every service will be released.',
    fun: 'No exclusions — everyone ships this time.',
  },
  'templates.excludeServicesPicker.countHint': {
    en: '{count} {noun} will be skipped from this release.',
    fun: '{count} {noun} sitting this release out.',
  },
  'templates.excludeServicesPicker.noun.singular': {
    en: 'service',
    fun: 'service',
  },
  'templates.excludeServicesPicker.noun.plural': {
    en: 'services',
    fun: 'services',
  },
  'templates.excludeServicesPicker.noOptions': {
    en: 'No gitops services discovered.',
    fun: 'No gitops services turned up.',
  },

  // GitopsServicePicker
  'templates.gitopsServicePicker.label': { en: 'Service', fun: 'Service' },
  'templates.gitopsServicePicker.loadingServices': {
    en: 'Loading services…',
    fun: 'Rounding up services…',
  },
  'templates.gitopsServicePicker.placeholder': {
    en: 'Search services…',
    fun: 'Search services…',
  },
  'templates.gitopsServicePicker.hint': {
    en: 'The service to promote.',
    fun: 'The service getting promoted.',
  },
  'templates.gitopsServicePicker.error': {
    en: 'Select a service.',
    fun: 'Pick a service first.',
  },

  // GitopsServicesPicker
  'templates.gitopsServicesPicker.label': { en: 'Services', fun: 'Services' },
  'templates.gitopsServicesPicker.loadingServices': {
    en: 'Loading services…',
    fun: 'Rounding up services…',
  },
  'templates.gitopsServicesPicker.placeholder': {
    en: 'Select services…',
    fun: 'Pick your lineup…',
  },
  'templates.gitopsServicesPicker.searchPlaceholder': {
    en: 'Search services…',
    fun: 'Search services…',
  },
  'templates.gitopsServicesPicker.defaultHint': {
    en: 'The services to promote.',
    fun: 'The services getting promoted.',
  },
  'templates.gitopsServicesPicker.countHint': {
    en: '{count} {noun} will be promoted — each gets its own approval.',
    fun: '{count} {noun} headed up — each one gets its own approval.',
  },
  'templates.gitopsServicesPicker.noun.singular': {
    en: 'service',
    fun: 'service',
  },
  'templates.gitopsServicesPicker.noun.plural': {
    en: 'services',
    fun: 'services',
  },
  'templates.gitopsServicesPicker.error': {
    en: 'Select at least one service.',
    fun: 'Pick at least one service.',
  },

  // PromoteEnvironmentPicker
  'templates.promoteEnvironmentPicker.label': {
    en: 'Target environment',
    fun: 'Target environment',
  },
  'templates.promoteEnvironmentPicker.placeholder': {
    en: 'Choose an environment',
    fun: 'Choose an environment',
  },

  // TicketRefsPicker
  'templates.ticketRefsPicker.label': {
    en: 'REG tickets',
    fun: 'REG tickets',
  },
  'templates.ticketRefsPicker.placeholder': {
    en: 'Type a ticket and press Enter…',
    fun: 'Type a ticket and press Enter…',
  },
  'templates.ticketRefsPicker.defaultHint': {
    en: 'Type a ticket (e.g. 1234 or REG-1234) and press Enter.',
    fun: 'Drop in a ticket (e.g. 1234 or REG-1234) and hit Enter.',
  },
  'templates.ticketRefsPicker.countHint': {
    en: '{count} {noun} — every REG-<n>.* tag found on a repo is cherry-picked, in version order.',
    fun: '{count} {noun} queued — every REG-<n>.* tag found on a repo gets cherry-picked, in version order.',
  },
  'templates.ticketRefsPicker.noun.singular': {
    en: 'ticket',
    fun: 'ticket',
  },
  'templates.ticketRefsPicker.noun.plural': {
    en: 'tickets',
    fun: 'tickets',
  },
  'templates.ticketRefsPicker.error': {
    en: 'Add at least one REG ticket.',
    fun: 'Add at least one REG ticket.',
  },

  // GitopsEnvConfigPicker
  'templates.gitopsEnvConfigPicker.connectGithub': {
    en: 'Connect GitHub first so Backstage can read regask/platform-gitops.',
    fun: 'Connect GitHub first so Backstage can peek at regask/platform-gitops.',
  },
  'templates.gitopsEnvConfigPicker.type.label': { en: 'Type', fun: 'Type' },
  'templates.gitopsEnvConfigPicker.type.placeholder': {
    en: 'Select a type',
    fun: 'Select a type',
  },
  'templates.gitopsEnvConfigPicker.service.label': {
    en: 'Service',
    fun: 'Service',
  },
  'templates.gitopsEnvConfigPicker.service.placeholder': {
    en: 'Select a service',
    fun: 'Select a service',
  },
  'templates.gitopsEnvConfigPicker.overlay.label': {
    en: 'Overlay',
    fun: 'Overlay',
  },
  'templates.gitopsEnvConfigPicker.overlay.placeholder': {
    en: 'Select an overlay',
    fun: 'Select an overlay',
  },

  // GitopsResourceConfigPicker
  'templates.gitopsResourceConfigPicker.connectGithub': {
    en: 'Connect GitHub first so Backstage can read regask/platform-gitops.',
    fun: 'Connect GitHub first so Backstage can peek at regask/platform-gitops.',
  },
  'templates.gitopsResourceConfigPicker.type.label': {
    en: 'Type',
    fun: 'Type',
  },
  'templates.gitopsResourceConfigPicker.type.placeholder': {
    en: 'Select a type',
    fun: 'Select a type',
  },
  'templates.gitopsResourceConfigPicker.service.label': {
    en: 'Service',
    fun: 'Service',
  },
  'templates.gitopsResourceConfigPicker.service.placeholder': {
    en: 'Select a service',
    fun: 'Select a service',
  },
  'templates.gitopsResourceConfigPicker.overlay.label': {
    en: 'Overlay',
    fun: 'Overlay',
  },
  'templates.gitopsResourceConfigPicker.overlay.placeholder': {
    en: 'Select an overlay',
    fun: 'Select an overlay',
  },
  'templates.gitopsResourceConfigPicker.helperDefault': {
    en: 'Choose type, service, and overlay to load current config.',
    fun: 'Pick type, service, and overlay to pull up the current config.',
  },
  'templates.gitopsResourceConfigPicker.helperCurrent': {
    en: 'Current: CPU {cpu}, memory {memory}, HPA {hpa}, min {min}, max {max}',
    fun: 'Right now: CPU {cpu}, memory {memory}, HPA {hpa}, min {min}, max {max}',
  },
  'templates.gitopsResourceConfigPicker.notSet': {
    en: 'not set',
    fun: 'not set',
  },
  'templates.gitopsResourceConfigPicker.hpaEnabledWord': {
    en: 'enabled',
    fun: 'enabled',
  },
  'templates.gitopsResourceConfigPicker.hpaDisabledWord': {
    en: 'disabled',
    fun: 'disabled',
  },
  'templates.gitopsResourceConfigPicker.cpuTier.label': {
    en: 'CPU tier',
    fun: 'CPU tier',
  },
  'templates.gitopsResourceConfigPicker.memoryTier.label': {
    en: 'Memory tier',
    fun: 'Memory tier',
  },
  'templates.gitopsResourceConfigPicker.tierHint': {
    en: 'Built-in tiers show request / limit. Choose custom to set exact values.',
    fun: 'Built-in tiers show request / limit. Go custom to dial in exact values.',
  },
  'templates.gitopsResourceConfigPicker.tierOption.keepCurrent': {
    en: 'keep current',
    fun: 'keep current',
  },
  'templates.gitopsResourceConfigPicker.tierOption.custom': {
    en: 'custom',
    fun: 'custom',
  },
  'templates.gitopsResourceConfigPicker.customCpuRequest.label': {
    en: 'Custom CPU request',
    fun: 'Custom CPU request',
  },
  'templates.gitopsResourceConfigPicker.customCpuLimit.label': {
    en: 'Custom CPU limit',
    fun: 'Custom CPU limit',
  },
  'templates.gitopsResourceConfigPicker.customMemoryRequest.label': {
    en: 'Custom memory request',
    fun: 'Custom memory request',
  },
  'templates.gitopsResourceConfigPicker.customMemoryLimit.label': {
    en: 'Custom memory limit',
    fun: 'Custom memory limit',
  },
  'templates.gitopsResourceConfigPicker.minReplicas.label': {
    en: 'Min replicas',
    fun: 'Min replicas',
  },
  'templates.gitopsResourceConfigPicker.maxReplicas.label': {
    en: 'Max replicas',
    fun: 'Max replicas',
  },
  'templates.gitopsResourceConfigPicker.hpaHintEnabled': {
    en: 'Only applies when the service enables HPA.',
    fun: 'Only kicks in when the service has HPA on.',
  },
  'templates.gitopsResourceConfigPicker.hpaHintDisabled': {
    en: 'This service does not enable HPA.',
    fun: 'This service has not switched HPA on.',
  },

  // GitopsSecretRefPicker
  'templates.gitopsSecretRefPicker.connectGithub': {
    en: 'Connect GitHub first so Backstage can read regask/platform-gitops.',
    fun: 'Connect GitHub first so Backstage can peek at regask/platform-gitops.',
  },
  'templates.gitopsSecretRefPicker.type.label': { en: 'Type', fun: 'Type' },
  'templates.gitopsSecretRefPicker.type.placeholder': {
    en: 'Select a type',
    fun: 'Select a type',
  },
  'templates.gitopsSecretRefPicker.service.label': {
    en: 'Service',
    fun: 'Service',
  },
  'templates.gitopsSecretRefPicker.service.placeholder': {
    en: 'Select a service',
    fun: 'Select a service',
  },
  'templates.gitopsSecretRefPicker.overlay.label': {
    en: 'Overlay',
    fun: 'Overlay',
  },
  'templates.gitopsSecretRefPicker.overlay.placeholder': {
    en: 'Select an overlay',
    fun: 'Select an overlay',
  },

  // UsvcMigrationBranchPicker
  'templates.usvcMigrationBranchPicker.label': {
    en: 'Branch',
    fun: 'Branch',
  },
  'templates.usvcMigrationBranchPicker.placeholder': {
    en: 'Search branches…',
    fun: 'Search branches…',
  },
  'templates.usvcMigrationBranchPicker.loading': {
    en: 'Loading branches…',
    fun: 'Rounding up branches…',
  },
  'templates.usvcMigrationBranchPicker.hint': {
    en: 'The regask/usvc-migrations branch to run migrations from.',
    fun: 'The regask/usvc-migrations branch this run pulls from.',
  },
  'templates.usvcMigrationBranchPicker.error': {
    en: 'Select a branch.',
    fun: 'Pick a branch first.',
  },
  'templates.usvcMigrationBranchPicker.refreshTooltip': {
    en: 'Reload branches',
    fun: 'Reload branches',
  },
  'templates.usvcMigrationBranchPicker.noOptions': {
    en: 'No matching branches',
    fun: 'Nothing to summon here',
  },

  // OnDemandMigrationPicker
  'templates.onDemandMigrationPicker.label': {
    en: 'Migration file',
    fun: 'Migration file',
  },
  'templates.onDemandMigrationPicker.placeholder': {
    en: 'Search migration files…',
    fun: 'Search migration files…',
  },
  'templates.onDemandMigrationPicker.hint': {
    en: 'On-demand migration file to run, newest first.',
    fun: 'On-demand migration to run, newest first.',
  },
  'templates.onDemandMigrationPicker.noBranch': {
    en: 'Choose a branch first.',
    fun: 'Pick a branch first.',
  },
  'templates.onDemandMigrationPicker.loading': {
    en: 'Loading migration files…',
    fun: 'Rounding up migration files…',
  },
  'templates.onDemandMigrationPicker.noFiles': {
    en: 'No on-demand migration files on this branch.',
    fun: 'No on-demand migrations on this branch.',
  },
  'templates.onDemandMigrationPicker.error': {
    en: 'Select a migration file.',
    fun: 'Pick a migration file first.',
  },
  'templates.onDemandMigrationPicker.refreshTooltip': {
    en: 'Reload migration files',
    fun: 'Reload migration files',
  },

  // SharedActionsVersionPicker
  'templates.sharedActionsVersionPicker.label': {
    en: 'Shared Actions release version',
    fun: 'Shared Actions release version',
  },
  'templates.sharedActionsVersionPicker.placeholder': {
    en: 'e.g. 2604',
    fun: 'e.g. 2604',
  },
  'templates.sharedActionsVersionPicker.loading': {
    en: 'Reading current version…',
    fun: 'Reading current version…',
  },
  'templates.sharedActionsVersionPicker.currentHint': {
    en: 'Current version is {current}. Change it to bump shared-actions with this production release — it is applied only when the release is approved.',
    fun: 'Current version is {current}. Change it to bump shared-actions with this production release — it lands only once the release is approved.',
  },
  'templates.sharedActionsVersionPicker.unchangedHint': {
    en: 'Leave unchanged to release without touching the shared-actions version.',
    fun: 'Leave as-is to ship without touching the shared-actions version.',
  },
  'templates.sharedActionsVersionPicker.loadError': {
    en: 'Could not read the current version — type a version number to set it.',
    fun: 'Could not read the current version — type a number to set it.',
  },
  'templates.sharedActionsVersionPicker.invalid': {
    en: 'Enter a positive whole number.',
    fun: 'Enter a positive whole number.',
  },
  'templates.sharedActionsVersionPicker.refreshTooltip': {
    en: 'Re-read current version',
    fun: 'Re-read current version',
  },
  // GithubRepoNamePicker
  'templates.githubRepoNamePicker.label': {
    en: 'Repository name',
    fun: 'Repository name',
  },
  'templates.githubRepoNamePicker.placeholder': {
    en: 'my-new-service',
    fun: 'my-new-service',
  },
  'templates.githubRepoNamePicker.hint': {
    en: 'Letters, digits, hyphens, underscores and periods.',
    fun: 'Letters, digits, hyphens, underscores and periods.',
  },
  'templates.githubRepoNamePicker.checking': {
    en: 'Checking the name against the organization…',
    fun: 'Asking GitHub if that name is taken…',
  },
  'templates.githubRepoNamePicker.available': {
    en: '{name} is available.',
    fun: '{name} is up for grabs.',
  },
  'templates.githubRepoNamePicker.taken': {
    en: 'That repository name is already taken.',
    fun: 'That name is already taken.',
  },
  'templates.githubRepoNamePicker.checkFailed': {
    en: 'Could not check the name — GitHub still rejects duplicates when the request is approved.',
    fun: 'Could not check the name — GitHub still blocks duplicates at approval time.',
  },
  'templates.githubRepoNamePicker.error': {
    en: 'A repository name is required.',
    fun: 'A repository name is required.',
  },
  // GithubRepoPropertiesPicker
  'templates.githubRepoPropertiesPicker.label': {
    en: 'Custom properties',
    fun: 'Custom properties',
  },
  'templates.githubRepoPropertiesPicker.hint': {
    en: "The organization's custom properties, read live from GitHub.",
    fun: "The org's own property flags, straight from GitHub.",
  },
  'templates.githubRepoPropertiesPicker.loading': {
    en: 'Reading organization properties…',
    fun: 'Fetching the org property flags…',
  },
  'templates.githubRepoPropertiesPicker.empty': {
    en: 'This organization defines no custom properties.',
    fun: 'No property flags on this org.',
  },
  'templates.githubRepoPropertiesPicker.selectNone': {
    en: 'Not set',
    fun: 'Not set',
  },
  'templates.githubRepoPropertiesPicker.refreshTooltip': {
    en: 'Re-read organization properties',
    fun: 'Re-read organization properties',
  },
};
